/**
 * Apply a ValidationResult to the JointJS graph.
 *
 * Routes through the same `handleObjectUpdateFn` callback that PropertyEditor
 * uses, so the canvas re-render path and persistence side-effects stay
 * identical to single-cell edits (per design §2.3).
 *
 * The applier never touches the graph directly — it only invokes the
 * caller-supplied `ObjectUpdateHandler`. This keeps unit tests free of
 * JointJS imports.
 */

import type {
  ApplyResult,
  ObjectUpdateHandler,
  ValidationResult,
} from '../model/types'
import type { GraphLike } from './exporter'

/**
 * For each `valid` record, compute the merged data payload and dispatch one
 * update call. `unmatched` and `error` records are skipped; counts include
 * everything that did NOT result in an apply.
 *
 * The graph is needed to look up the current `data.typeId`, which we keep on
 * the merged payload so downstream consumers (logging, persistence) keep the
 * type association.
 */
export function applyValidationResult(
  result: ValidationResult,
  graph: GraphLike,
  handleObjectUpdate: ObjectUpdateHandler
): ApplyResult {
  let applied = 0
  let skipped = 0

  // Build a quick id → element map once.
  const elementsById = new Map<string, ReturnType<GraphLike['getElements']>[number]>()
  for (const el of graph.getElements()) {
    elementsById.set(String(el.id), el)
  }

  for (const validated of result.records) {
    if (validated.status !== 'valid' || !validated.diff) {
      skipped += 1
      continue
    }
    const element = elementsById.get(validated.diff.id)
    if (!element) {
      // Defensive — should not happen because validator already verified existence,
      // but graphs can mutate between validate-time and apply-time.
      skipped += 1
      continue
    }

    // CRITICAL: handleObjectUpdate does `element.set('data', updates.data)` —
    // a full REPLACE, not a merge. So we MUST send the entire merged data
    // payload, otherwise we wipe layer / entityHandle / vertices / originalPosition
    // and break CSV-imported elements (their typeId is also resolved out of
    // those fields). Mirrors PropertyEditor.handlePropertyChange:142-153.
    const existingData = (element.get('data') ?? {}) as Record<string, unknown>
    // The Excel sheet was generated from spec.typeId, which always corresponds
    // to a real ObjectType — persist it on the element so OBJECT TYPE shows in
    // PropertyEditor immediately after apply (before this, elements that only
    // resolved typeId via layerTypeMappings store ended up with empty type).
    const existingTypeId =
      typeof existingData.typeId === 'string' ? existingData.typeId : undefined
    const persistedTypeId: string | undefined =
      validated.record.typeId || existingTypeId

    handleObjectUpdate(validated.diff.id, {
      data: {
        ...existingData,
        ...(persistedTypeId ? { typeId: persistedTypeId } : {}),
        properties: validated.diff.after,
      },
    })
    applied += 1
  }

  return { applied, skipped }
}
